// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/hooks/Breakpoints.tsx
// Version: v1.0.1
// Description: Responsive Breakpoint Hooks für Mobile/Tablet/Desktop Detection
import { useEffect, useState } from 'react';

// ESTATA Breakpoints - Konsistent mit Tailwind
export const BREAKPOINTS = {
  SM: 640,    // Tailwind sm:
  MD: 768,    // Tailwind md:
  LG: 1024,   // Tailwind lg:
  XL: 1280,   // Tailwind xl:
  '2XL': 1510, // Tailwind 2xl:
  
  // ESTATA Custom
  DESKTOP: 1200,  // Für Header Desktop/Mobile Switch
  TABLET: 768,
  MOBILE: 640
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

// Basis Hook für Breakpoint Detection
export const useBreakpoint = (breakpoint: number): boolean => {
  const [matches, setMatches] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth >= breakpoint
  );

  useEffect(() => {
    const handleResize = (): void => {
      setMatches(window.innerWidth >= breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return matches;
};

// Convenience Hooks für häufig verwendete Breakpoints
export const useIsDesktop = (): boolean => useBreakpoint(BREAKPOINTS.DESKTOP);
export const useIsTablet = (): boolean => useBreakpoint(BREAKPOINTS.TABLET);
export const useIsMobile = (): boolean => !useBreakpoint(BREAKPOINTS.MOBILE);

// Erweiterte Hook für mehrere Breakpoints
export const useBreakpoints = () => {
  const [breakpoints, setBreakpoints] = useState(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: false,
        isLarge: false,
        current: 'mobile' as const
      };
    }

    const width = window.innerWidth;
    return {
      isMobile: width < BREAKPOINTS.MOBILE,
      isTablet: width >= BREAKPOINTS.TABLET && width < BREAKPOINTS.DESKTOP,
      isDesktop: width >= BREAKPOINTS.DESKTOP,
      isLarge: width >= BREAKPOINTS.XL,
      current: width >= BREAKPOINTS.DESKTOP 
        ? 'desktop' 
        : width >= BREAKPOINTS.TABLET 
          ? 'tablet' 
          : 'mobile' as const
    };
  });

  useEffect(() => {
    const handleResize = (): void => {
      const width = window.innerWidth;
      setBreakpoints({
        isMobile: width < BREAKPOINTS.MOBILE,
        isTablet: width >= BREAKPOINTS.TABLET && width < BREAKPOINTS.DESKTOP,
        isDesktop: width >= BREAKPOINTS.DESKTOP,
        isLarge: width >= BREAKPOINTS.XL,
        current: width >= BREAKPOINTS.DESKTOP 
          ? 'desktop' 
          : width >= BREAKPOINTS.TABLET 
            ? 'tablet' 
            : 'mobile'
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoints;
};

// Hook für aktuelle Bildschirmbreite
export const useScreenWidth = (): number => {
  const [width, setWidth] = useState<number>(
    () => typeof window !== 'undefined' ? window.innerWidth : 0
  );

  useEffect(() => {
    const handleResize = (): void => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};